import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowsClockwise, Lightning } from "@phosphor-icons/react";
import api from "../../lib/api";
import { fmtDate } from "../../lib/format";

const SOURCES = [
  { key: "", label: "All" },
  { key: "stripe", label: "Stripe" },
  { key: "yesim", label: "Yesim" },
];

const badge = (src) =>
  src === "stripe"
    ? "bg-cobalt-50 text-cobalt-600"
    : src === "yesim"
    ? "bg-coral-soft/70 text-coral-deep"
    : "bg-ink-100 text-ink-700";

export default function AdminWebhooks() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = () => {
    setLoading(true);
    const q = source ? `?source=${source}` : "";
    api.get(`/admin/webhooks${q}`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [source]); // eslint-disable-line

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div data-testid="admin-webhooks">
      <div className="overline mb-2">{t("admin.webhooks", "Webhook log")}</div>
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tightest mb-8">
        {t("admin.webhooksTitle", "Webhook events")}
      </h1>
      <div className="mb-6 flex items-center gap-3">
        {SOURCES.map((s) => (
          <button
            key={s.key || "all"}
            type="button"
            onClick={() => setSource(s.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
              source === s.key ? "bg-cobalt-500 text-white border-cobalt-500" : "bg-white border-ink-200 hover:border-cobalt-300"
            }`}
            data-testid={`webhook-filter-${s.key || "all"}`}
          >
            {s.label}
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          className="ml-auto text-xs font-semibold flex items-center gap-1.5 text-cobalt-600 hover:underline"
          data-testid="webhooks-refresh"
        >
          <ArrowsClockwise size={14} weight="bold" /> {t("common.refresh", "Refresh")}
        </button>
      </div>

      <div className="card-soft !rounded-3xl overflow-hidden">
        {loading && <div className="px-6 py-10 text-center text-ink-500">{t("common.loading")}</div>}
        {!loading && items.length === 0 && (
          <div className="px-6 py-16 text-center text-ink-500" data-testid="webhooks-empty">
            <Lightning size={32} weight="bold" className="text-ink-300 inline-block mb-3" />
            <div>{t("admin.webhooksEmpty", "No webhook events received yet.")}</div>
          </div>
        )}
        <ul className="divide-y divide-ink-100">
          {items.map((it) => {
            const isOpen = !!expanded[it.id];
            return (
              <li key={it.id} className="hover:bg-sky-soft/40" data-testid={`webhook-row-${it.id}`}>
                <button
                  type="button"
                  onClick={() => toggle(it.id)}
                  className="w-full text-left px-5 py-4 flex items-center gap-4"
                >
                  <span className={`pill text-[10px] uppercase tracking-widest ${badge(it.source)}`}>
                    {it.source}
                  </span>
                  <span className="font-mono text-xs text-ink-700 flex-1 truncate">{it.type || "—"}</span>
                  <span className="text-xs text-ink-500 hidden sm:block">{fmtDate(it.created_at)}</span>
                  <span className="text-ink-300">{isOpen ? "▾" : "▸"}</span>
                </button>
                {isOpen && (
                  <pre className="px-5 pb-5 -mt-1 bg-ink-50 text-[11px] leading-relaxed text-ink-700 overflow-x-auto">
                    {JSON.stringify(it, null, 2)}
                  </pre>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
